/**
 * Avatar com fallback determinístico. Sem imagem, gera-se uma inicial sobre uma
 * cor derivada do id — determinística, portanto a mesma pessoa tem sempre a
 * mesma cor em todas as sessões e em todas as máquinas.
 */

import type { PublicUser } from "@greenvoice/shared";
import type { CSSProperties } from "react";
import { Selo } from "./Selo";

/** O nome de cada estado. Vive com a geometria; sai daqui por comodidade de quem
 *  já o importava do avatar. */
export { STATUS_LABEL } from "./seloDeEstado";

/**
 * As cores dos avatares sem imagem.
 *
 * Eram oito cores de saturação máxima — o roxo, o rosa-choque, o laranja e o
 * vermelho de qualquer paleta por omissão. Numa lista de vinte pessoas aquilo
 * lê-se como uma caixa de lápis: cada avatar puxa o olho com a mesma força, e
 * nenhum deles tem nada que ver com o verde da casa.
 *
 * Estas oito têm a MESMA luminosidade percebida e saturação a meio caminho, à
 * volta do verde da marca. Continuam a distinguir-se umas das outras (é para
 * isso que existem), mas em conjunto formam uma paleta em vez de um alarme —
 * e uma inicial branca por cima lê-se em todas.
 */
const PALETTE = ["#3f9a6a", "#4a8f9e", "#5b83c4", "#7d7ac0", "#a0759e", "#b07a63", "#948a4e", "#5f9c8a"] as const;

function hue(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return PALETTE[hash % PALETTE.length]!;
}

const initial = (name: string): string => (name.trim()[0] ?? "?").toUpperCase();

interface AvatarProps {
  user: Pick<PublicUser, "id" | "username" | "displayName" | "avatarUrl" | "status">;
  /** A moldura à volta, quando a pessoa escolheu uma. Só o `ProfileCard` a
   *  passa: numa lista de trinta membros, trinta anéis de cor não são uma
   *  decoração, são um sarilho — a moldura é para onde o perfil é o assunto. */
  frame?: { colors: readonly [string, string] } | null;
  /** Sobrepõe o avatar do utilizador (avatar por servidor). */
  overrideUrl?: string | null;
  size?: number;
  showStatus?: boolean;
  speaking?: boolean;
}

export function Avatar({ user, overrideUrl, size = 32, showStatus = false, speaking = false, frame }: AvatarProps) {
  const url = overrideUrl ?? user.avatarUrl;
  const name = user.displayName ?? user.username;

  return (
    <span
      className={`avatar${speaking ? " avatar--speaking" : ""}${frame ? " avatar--moldura" : ""}`}
      style={
        frame
          ? ({
              width: size,
              height: size,
              "--moldura-a": frame.colors[0],
              "--moldura-b": frame.colors[1],
            } as CSSProperties)
          : { width: size, height: size }
      }
    >
      {url ? (
        <img src={url} alt="" width={size} height={size} loading="lazy" decoding="async" />
      ) : (
        <span className="avatar__fallback" style={{ background: hue(user.id), fontSize: Math.round(size * 0.42) }}>
          {initial(name)}
        </span>
      )}
      {/* O estado lê-se pela FORMA (lua, traço, anel) e não só pela cor — quem
          não distingue as duas fica na mesma com um `title` sozinho. */}
      {showStatus ? <Selo status={user.status} className="avatar__status" /> : null}
    </span>
  );
}
